import {
  ChatBubbleOvalLeftEllipsisIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { SnowFlake as SnowFlakeIcon } from '@praetorian-chariot/ui';
import { ReactNode } from 'react';

import { RiskSeverity, RiskStatus, SeedStatus } from '@/types';

/**
 * Base action option type
 */
type BaseType = {
  type?: 'divider' | 'action' | 'comment';
  count?: string;
};

/**
 * Action type for seed status updates
 */
type ActionType = {
  type?: 'action';
  icon: ReactNode;
  label: string;
  status: SeedStatus | RiskStatus | RiskSeverity;
  count?: string;
};

/**
 * Divider type for visual separation
 */
type DividerType = BaseType & {
  type: 'divider';
  icon?: ReactNode;
  label?: string;
  status?: SeedStatus;
};

/**
 * Comment type for adding notes
 */
type CommentType = BaseType & {
  type: 'comment';
  icon?: ReactNode;
  label?: string;
  status?: SeedStatus;
};

/**
 * Action option for seed action menus
 */
export type ActionOptionProp = ActionType | DividerType | CommentType;

/**
 * Seed action menu options
 *
 * Available actions:
 * - Approve (⌥ A)
 * - Reject (⌥ R)
 * - Freeze (⌥ F)
 * - Add Note
 * - Delete (⌥ D)
 *
 * @example
 * ```tsx
 * import { ActionOptions } from '@/sections/seeds/config/actions';
 *
 * const menu = ActionOptions.map(option => {
 *   if (option.type === 'divider') return <Divider />;
 *   if (option.type === 'comment') return <CommentAction />;
 *   return <ActionButton {...option} />;
 * });
 * ```
 */
export const ActionOptions: ActionOptionProp[] = [
  {
    icon: <CheckCircleIcon className="size-5 cursor-pointer" />,
    label: 'Approve',
    status: SeedStatus.Accept,
    count: '⌥ A',
  },
  {
    icon: <XCircleIcon className="size-5 cursor-pointer" />,
    label: 'Reject',
    status: SeedStatus.Reject,
    count: '⌥ R',
  },
  {
    icon: <SnowFlakeIcon className="size-5 cursor-pointer" />,
    label: 'Freeze',
    status: SeedStatus.Freeze,
    count: '⌥ F',
  },
  {
    label: 'Add Note',
    icon: <ChatBubbleOvalLeftEllipsisIcon className="size-5" />,
    type: 'comment',
  },
  {
    type: 'divider',
  },
  {
    icon: <TrashIcon className="size-5 cursor-pointer" />,
    label: 'Delete',
    status: SeedStatus.Delete,
    count: '⌥ D',
  },
];
